/**
 * ContainerResult Component
 *
 * Displays found container details and action selection (swap/pickup).
 * Shows container size, delivery date, address, and current status.
 */

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { PackageIcon, CalendarIcon, MapPinIcon, CheckCircleIcon } from 'lucide-react';
import { OrderDTO } from '../../types/order';

interface ContainerResultProps {
  order: OrderDTO;
  selectedAction: 'swap' | 'pickup' | null;
  onActionSelect: (action: 'swap' | 'pickup') => void;
}

/**
 * Format container size for display
 * Example: '20yd' -> '20 Yard'
 */
function formatContainerSize(size: string): string {
  const match = size.match(/^(\d+)(yd|y)?$/i);
  if (!match) return size;
  return `${match[1]} Yard`;
}

/**
 * Format date string for display
 * Example: '2024-10-10' -> 'Oct 10, 2024'
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Get status badge color
 */
function getStatusColor(status: string): string {
  switch (status) {
    case 'confirmed':
      return 'bg-blue-100 text-blue-800';
    case 'scheduled':
      return 'bg-green-100 text-green-800';
    case 'completed':
      return 'bg-gray-100 text-gray-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function ContainerResult({ order, selectedAction, onActionSelect }: ContainerResultProps) {
  return (
    <div className="space-y-6 mt-6">
      {/* Container Details Card */}
      <Card className="border-primary-200 bg-primary-50/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Container Found!</CardTitle>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </span>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Container Size */}
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <PackageIcon className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Container Size</p>
              <p className="font-semibold text-gray-900">
                {formatContainerSize(order.containerSize)}
              </p>
            </div>
          </div>

          {/* Delivery Date */}
          {order.deliveryDate && (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Delivered</p>
                <p className="font-semibold text-gray-900">
                  {formatDate(order.deliveryDate)}
                </p>
              </div>
            </div>
          )}

          {/* Service Address */}
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <MapPinIcon className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Location</p>
              <p className="font-semibold text-gray-900">
                {order.serviceAddress.street}
              </p>
              <p className="text-sm text-gray-600">
                {order.serviceAddress.city}, {order.serviceAddress.state} {order.serviceAddress.zipCode}
              </p>
            </div>
          </div>

          {/* Order Number */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Order Number: <span className="font-mono font-medium">{order.orderNumber}</span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Action Selection Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">What do you need?</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="space-y-3">
            {/* Swap Option */}
            <button
              type="button"
              onClick={() => onActionSelect('swap')}
              className={`
                w-full p-4 rounded-lg border-2 text-left transition-all
                ${selectedAction === 'swap'
                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }
              `}
              aria-pressed={selectedAction === 'swap'}
            >
              <div className="flex items-start gap-3">
                <div className={`
                  flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mt-1
                  ${selectedAction === 'swap'
                    ? 'border-primary-500 bg-primary-500'
                    : 'border-gray-300 bg-white'
                  }
                `}>
                  {selectedAction === 'swap' && (
                    <CheckCircleIcon className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 mb-1">
                    Swap it out (full for empty)
                  </p>
                  <p className="text-sm text-gray-600">
                    We&apos;ll bring an empty container and take the full one away
                  </p>
                </div>
              </div>
            </button>

            {/* Pickup Option */}
            <button
              type="button"
              onClick={() => onActionSelect('pickup')}
              className={`
                w-full p-4 rounded-lg border-2 text-left transition-all
                ${selectedAction === 'pickup'
                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }
              `}
              aria-pressed={selectedAction === 'pickup'}
            >
              <div className="flex items-start gap-3">
                <div className={`
                  flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mt-1
                  ${selectedAction === 'pickup'
                    ? 'border-primary-500 bg-primary-500'
                    : 'border-gray-300 bg-white'
                  }
                `}>
                  {selectedAction === 'pickup' && (
                    <CheckCircleIcon className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 mb-1">
                    Pick it up (remove it)
                  </p>
                  <p className="text-sm text-gray-600">
                    We&apos;ll remove your container completely
                  </p>
                </div>
              </div>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
